import type { Article } from "../insights";
import { ctaPl, ctaEn, ctaZh } from "../insight-cta";

export const employerOfRecordPolska: Article = {
  slug: "employer-of-record-polska",
  updated: "2026-03-18",
  updatedLabel: {
    pl: "Aktualizacja: 18 marca 2026",
    en: "Last updated: 18 March 2026",
    zh: "最近更新：2026 年 3 月 18 日",
  },
  topic: { pl: "Zatrudnianie", en: "Employment", zh: "用工模式" },
  pl: {
    title: "Employer of Record w Polsce: jak zatrudnić programistę bez spółki",
    h1: "Employer of Record w Polsce 2026: EOR, B2B czy własna spółka",
    metaTitle: "Employer of Record Polska 2026: koszty, ryzyko, proces",
    metaDescription:
      "Zatrudnienie programisty w Polsce bez spółki: EOR vs B2B vs własna sp. z o.o. vs staff augmentation. Realne koszty, ryzyko reklasyfikacji i harmonogram 30 dni.",
    eyebrow: "Insights · Zatrudnianie",
    summary:
      "EOR, B2B, własna spółka czy staff augmentation - porównanie kosztów, ryzyka i czasu wdrożenia dla firm bez podmiotu w Polsce.",
    lead:
      "Coraz więcej firm z Europy Zachodniej i USA chce zatrudniać inżynierów w Polsce, nie mając tu żadnej struktury prawnej. Prowadzimy rekrutację IT z Krakowa na cały kraj i region CEE, więc regularnie doradzamy klientom, który model zatrudnienia ma sens przy pierwszym wynajmowanym stanowisku, a który dopiero przy dziesiątym. Poniżej realny przegląd czterech ścieżek: employer of record, kontrakt B2B, własna spółka i staff augmentation.",
    note:
      "To materiał informacyjny, nie porada prawna ani podatkowa. Kwoty to widełki planistyczne w PLN/EUR na 2026 rok - przed decyzją skonsultuj się z lokalnym doradcą podatkowym lub kancelarią prawa pracy.",
    readTime: "8 min czytania",
    takeaways: [
      "EOR w Polsce onboarduje pracownika w 5-10 dni roboczych, założenie własnej spółki zajmuje 6-10 tygodni.",
      "Typowa opłata EOR to 8-15% wynagrodzenia lub 300-800 EUR miesięcznie za osobę, płatne oprócz kosztów zatrudnienia.",
      "Składki pracodawcy w Polsce (ZUS, FP, FGŚP) to ok. 20% ponad wynagrodzenie brutto, niezależnie od modelu zatrudnienia.",
      "Powyżej ok. 10 zatrudnionych osób własna sp. z o.o. zwykle wychodzi taniej niż EOR w horyzoncie 2-3 lat.",
      "Kontrakt B2B źle skonstruowany (stałe godziny, jeden klient, brak ryzyka gospodarczego) grozi reklasyfikacją na umowę o pracę z zaległymi składkami i karami.",
      "Większość senior developerów w Polsce preferuje kontrakt B2B - to często warunek konieczny do domknięcia oferty, nie dodatek.",
    ],
    sections: [
      {
        id: "modele",
        eyebrow: "01 / Modele",
        title: "Cztery ścieżki zatrudnienia w Polsce bez lokalnego podmiotu",
        blocks: [
          {
            kind: "table",
            caption: "Porównanie modeli zatrudnienia, Polska 2026",
            head: ["Model", "Czas wdrożenia", "Koszt dodatkowy", "Kontrola", "Ryzyko", "Najlepsze dla"],
            rows: [
              [
                "Employer of Record (EOR)",
                "5-10 dni roboczych",
                "8-15% wynagrodzenia lub 300-800 EUR/mc",
                "Średnia - EOR jest formalnym pracodawcą",
                "Niskie, jeśli dostawca ma realną obecność w Polsce",
                "1-9 osób, testowanie rynku, szybki start",
              ],
              [
                "Kontrakt B2B (bezpośrednio)",
                "1-3 dni robocze",
                "Brak stałej opłaty, ale wyższa stawka netto",
                "Wysoka - umowa cywilnoprawna, mniej obowiązków pracodawcy",
                "Reklasyfikacja przy błędnej konstrukcji umowy",
                "Kontraktorów, senior IC, krótkie i średnie zaangażowania",
              ],
              [
                "Własna spółka (sp. z o.o.)",
                "6-10 tygodni",
                "Koszty księgowości, kadr, compliance",
                "Pełna",
                "Odpowiedzialność prawna i administracyjna po stronie firmy",
                "Powyżej ok. 10 osób, długoterminowa obecność w Polsce",
              ],
              [
                "Staff augmentation / wynajem specjalistów",
                "1-2 tygodnie",
                "Marża dostawcy 20-40% ponad koszt pracownika",
                "Niska - zespół zarządzany operacyjnie, ale formalnie zatrudniony przez dostawcę",
                "Rotacja, brak przywiązania kandydata do marki klienta",
                "Wzmocnienie zespołu na czas projektu, elastyczność liczby osób",
              ],
            ],
          },
          {
            kind: "p",
            text: "Nie ma jednego „dobrego” modelu - jest model dopasowany do skali. Firma zatrudniająca pierwszą osobę w Polsce prawie zawsze zaczyna od EOR albo B2B, bo koszt i czas założenia spółki nie mają uzasadnienia przy jednym etacie. Dopiero gdy zespół rośnie, arytmetyka się zmienia.",
          },
        ],
      },
      {
        id: "koszty",
        eyebrow: "02 / Koszty",
        title: "Ile to naprawdę kosztuje: trzy przykłady",
        blocks: [
          {
            kind: "table",
            caption: "Całkowity koszt zatrudnienia w Polsce, przykładowe wynagrodzenia brutto (PLN/mc, 2026)",
            head: ["Wynagrodzenie brutto (UoP)", "Całkowity koszt pracodawcy (UoP)", "Całkowity koszt przez EOR", "Ekwiwalent B2B (netto faktury)"],
            rows: [
              ["15 000 PLN", "ok. 18 000-18 300 PLN", "ok. 19 500-21 300 PLN", "ok. 16 500-18 000 PLN"],
              ["25 000 PLN", "ok. 30 000-30 500 PLN", "ok. 32 500-35 000 PLN", "ok. 27 000-29 500 PLN"],
              ["35 000 PLN", "ok. 42 000-42 700 PLN", "ok. 45 000-48 500 PLN", "ok. 37 500-41 000 PLN"],
            ],
          },
          {
            kind: "p",
            text: "Koszt pracodawcy na umowie o pracę to wynagrodzenie brutto plus ok. 20% składek ZUS, Fundusz Pracy i FGŚP. EOR dokłada do tego swoją opłatę (traktujemy ją tu jako procent od kosztu bazowego, realnie część dostawców liczy stałą stawkę miesięczną - to zwykle taniej przy wyższych wynagrodzeniach). B2B wygląda pozornie najtaniej, ale kandydat sam pokrywa swój ZUS i ubezpieczenie - stąd wyższa stawka na fakturze niż wynikałoby to z prostego przeliczenia brutto-netto.",
          },
          {
            kind: "p",
            text: "Do pełnego rachunku trzeba doliczyć 26 dni urlopu wypoczynkowego rocznie na umowie o pracę (na B2B strony ustalają to umownie, zwykle 20-26 dni płatnej przerwy jako element negocjacji) oraz PPK - pracowniczy plan kapitałowy, do którego pracodawca dopłaca standardowo 1,5% wynagrodzenia brutto, jeśli pracownik nie zrezygnował z uczestnictwa.",
          },
        ],
      },
      {
        id: "ryzyko",
        eyebrow: "03 / Ryzyko",
        title: "Cztery ryzyka, o których trzeba wiedzieć przed podpisaniem umowy",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Reklasyfikacja kontraktu B2B",
                meta: "Najczęstsze ryzyko",
                body: "Jeśli kontraktor pracuje wyłącznie dla jednego klienta, na stałych godzinach, pod bezpośrednim nadzorem i bez własnego ryzyka gospodarczego, urząd może uznać relację za ukryty stosunek pracy. Skutek: zaległe składki ZUS, podatek i kary za obie strony.",
              },
              {
                title: "Zakład podatkowy (permanent establishment)",
                meta: "Ryzyko dla spółki-matki",
                body: "Zatrudnienie pracownika w Polsce, który faktycznie zawiera umowy lub prowadzi działalność operacyjną w imieniu zagranicznej spółki, może wygenerować obowiązek podatkowy tej spółki w Polsce, nawet bez lokalnego oddziału. EOR dobrze skonstruowany minimalizuje to ryzyko, ale nie eliminuje go automatycznie przy roli sprzedażowej czy decyzyjnej.",
              },
              {
                title: "Przeniesienie praw autorskich (IP assignment)",
                meta: "Kluczowe dla firm produktowych",
                body: "W polskim prawie autorskim przeniesienie praw do kodu musi być wyraźnie zapisane w umowie - nie następuje automatycznie przez sam fakt zatrudnienia, zwłaszcza na B2B. Brak klauzuli IP assignment to jedna z najczęstszych luk w umowach podpisywanych bez lokalnego doradcy.",
              },
              {
                title: "Ochrona danych i RODO",
                meta: "Dotyczy każdego modelu",
                body: "Niezależnie od formy zatrudnienia firma odpowiada za zgodność z RODO w zakresie danych, do których pracownik ma dostęp. Przy EOR trzeba jasno rozdzielić, kto jest administratorem, a kto podmiotem przetwarzającym dane kadrowe.",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / Proces",
        title: "Od oferty do pierwszego dnia pracy: plan na 30 dni",
        blocks: [
          {
            kind: "list",
              items: [
              "Dni 1-5: wybór modelu (EOR, B2B, własna spółka) i - jeśli EOR - podpisanie umowy z dostawcą, który ma realną obecność operacyjną w Polsce, nie tylko biuro wirtualne.",
              "Dni 3-10: negocjacja warunków z kandydatem - forma umowy, widełki, świadczenia pozapłacowe, ekwiwalent urlopowy, sprzęt roboczy.",
              "Dni 8-12: przygotowanie umowy przez EOR lub dział prawny (przy B2B) wraz z klauzulą IP assignment i klauzulami poufności.",
              "Dni 10-15: onboarding formalny - zgłoszenie do ZUS (przy UoP przez EOR), badania wstępne BHP, konfiguracja sprzętu i dostępów.",
              "Dni 15-25: onboarding merytoryczny - dostęp do repozytoriów, narzędzi, pierwszego sprintu; równolegle konfiguracja PPK, jeśli dotyczy.",
              "Dzień 30: pracownik w pełni operacyjny, pierwsza wypłata rozliczona zgodnie z polskim kalendarzem płatności (zwykle do 10. dnia miesiąca za miesiąc poprzedni).",
            ],
          },
          {
            kind: "p",
            text: "Dla porównania: założenie własnej sp. z o.o. w Polsce to zwykle 6-10 tygodni, licząc rejestrację w KRS, otwarcie konta firmowego, rejestrację jako płatnik ZUS i skonfigurowanie księgowości - i to zanim pierwsza osoba w ogóle zostanie zatrudniona.",
          },
        ],
      },
      {
        id: "kandydat",
        eyebrow: "05 / Kandydat",
        title: "Czego oczekują kandydaci w Polsce",
        blocks: [
          {
            kind: "p",
            text: "Większość senior developerów, z którymi rozmawiamy, traktuje kontrakt B2B nie jako opcję, tylko jako warunek rozmowy o ofercie. Powód jest prosty: niższy efektywny podatek (ryczałt lub liniowy 19% zamiast progresywnej skali PIT) i elastyczność w rozliczaniu kosztów działalności.",
          },
          {
            kind: "list",
            items: [
              "Możliwość wyboru między B2B a umową o pracę - brak opcji B2B odrzuca część senior kandydatów jeszcze przed rozmową o widełkach.",
              "Terminy płatności do 7-10 dni od wystawienia faktury na B2B, nie 30-45 dni - to często pytanie zadawane wprost w pierwszej rozmowie.",
              "Sprzęt służbowy dostarczony przed pierwszym dniem, nie w drugim tygodniu.",
              "Ekwiwalent urlopowy porównywalny z 26 dniami na UoP, nawet jeśli formalnie umowa tego nie wymaga.",
              "Jasność co do PPK i świadczeń pozapłacowych pozapłacowych (prywatna opieka medyczna, karta sportowa) już na etapie oferty, nie po podpisaniu umowy.",
            ],
          },
        ],
      },
    ],
    faqTitle: "Najczęstsze pytania",
    faq: [
      {
        q: "Czy EOR w Polsce jest legalny?",
        a: "Tak, employer of record to legalny model, w którym lokalny podmiot formalnie zatrudnia pracownika na rzecz zagranicznej firmy. Kluczowe jest, by dostawca EOR miał realną rejestrację i infrastrukturę kadrową w Polsce, a nie działał jako pusta struktura.",
      },
      {
        q: "Kiedy własna spółka opłaca się bardziej niż EOR?",
        a: "Zwykle powyżej ok. 10 zatrudnionych osób w horyzoncie 2-3 lat, gdy stała opłata EOR pomnożona przez liczbę osób przewyższa koszty prowadzenia własnej spółki i działu kadr.",
      },
      {
        q: "Czy mogę zatrudnić programistę w Polsce tylko na B2B?",
        a: "Tak, to najpopularniejsza forma wśród senior specjalistów IT, pod warunkiem że umowa realnie odzwierciedla niezależność kontraktora - własne ryzyko gospodarcze, elastyczne godziny, możliwość pracy dla innych klientów.",
      },
      {
        q: "Jak długo trwa proces od oferty do pierwszego dnia pracy przez EOR?",
        a: "Przy sprawnym dostawcy EOR realnie 5-10 dni roboczych na stronę formalną, plus czas potrzebny kandydatowi na wypowiedzenie u obecnego pracodawcy - w Polsce to zwykle 1-3 miesiące przy umowie o pracę.",
      },
      {
        q: "Co grozi za błędnie skonstruowany kontrakt B2B?",
        a: "Przy reklasyfikacji na umowę o pracę firma może zostać zobowiązana do zapłaty zaległych składek ZUS i podatku wraz z odsetkami, a w niektórych przypadkach także do kar administracyjnych. To ryzyko rośnie wraz z liczbą kontraktorów pracujących na wyłączność.",
      },
    ],
    cta: ctaPl,
    methodology:
      "Metodologia: dane kosztowe i harmonogramy opieramy na procesach zatrudnienia prowadzonych z klientami Positive Recruitment w latach 2025-2026, skonfrontowanych z publicznymi raportami płacowymi polskiego rynku IT. To materiał informacyjny, nie porada prawna ani podatkowa - traktuj podane kwoty i terminy jako zakresy planistyczne.",
  },
  en: {
    title: "Employer of Record in Poland: hiring a developer without a local entity",
    h1: "Employer of Record in Poland 2026: EOR vs B2B vs own entity",
    metaTitle: "Employer of Record Poland 2026: Costs, Risk, Process",
    metaDescription:
      "Hire developers in Poland without setting up a company: EOR vs B2B vs own sp. z o.o. vs staff augmentation. Real costs, reclassification risk and a 30-day timeline.",
    eyebrow: "Insights · Employment",
    summary:
      "EOR, B2B, an own entity or staff augmentation - a cost, risk and timeline comparison for companies without a legal presence in Poland.",
    lead:
      "More companies from Western Europe and the US want to hire engineers in Poland without any legal structure here. We run IT recruitment out of Kraków across the whole country and the CEE region, so we regularly advise clients on which employment model makes sense for the first hire in Poland, and which one only pays off at the tenth. Here is a real-world walkthrough of the four paths: employer of record, B2B contracting, your own entity, and staff augmentation.",
    note:
      "This is general information, not legal or tax advice. Figures are planning ranges in PLN/EUR for 2026; consult a local tax advisor or employment lawyer before deciding.",
    readTime: "8 min read",
    takeaways: [
      "An EOR in Poland can onboard an employee in 5-10 working days; setting up your own entity takes 6-10 weeks.",
      "A typical EOR fee is 8-15% of salary, or 300-800 EUR per employee per month, on top of employment costs.",
      "Employer contributions in Poland (social security, labour fund, guarantee fund) add roughly 20% on top of gross salary, regardless of the model.",
      "Above roughly 10 employees, your own sp. z o.o. usually becomes cheaper than an EOR over a 2-3 year horizon.",
      "A poorly structured B2B contract (fixed hours, one client, no business risk) risks reclassification into an employment relationship, with back-payment of contributions and penalties.",
      "Most senior developers in Poland prefer a B2B contract - it is often a precondition to close the offer, not a nice-to-have.",
    ],
    sections: [
      {
        id: "modele",
        eyebrow: "01 / Models",
        title: "Four paths to hiring in Poland without a local entity",
        blocks: [
          {
            kind: "table",
            caption: "Employment model comparison, Poland 2026",
            head: ["Model", "Setup time", "Extra cost", "Control", "Risk", "Best for"],
            rows: [
              [
                "Employer of Record (EOR)",
                "5-10 working days",
                "8-15% of salary or 300-800 EUR/mo",
                "Medium - the EOR is the formal employer",
                "Low, if the provider has a genuine presence in Poland",
                "1-9 people, market testing, fast start",
              ],
              [
                "B2B contract (direct)",
                "1-3 working days",
                "No fixed fee, but a higher net rate",
                "High - a civil-law contract, fewer employer duties",
                "Reclassification if structured incorrectly",
                "Contractors, senior ICs, short to mid engagements",
              ],
              [
                "Own entity (sp. z o.o.)",
                "6-10 weeks",
                "Accounting, HR and compliance overhead",
                "Full",
                "Legal and administrative liability sits with the company",
                "Above roughly 10 people, long-term presence in Poland",
              ],
              [
                "Staff augmentation / wynajem specjalistów",
                "1-2 weeks",
                "Provider margin of 20-40% on top of employee cost",
                "Low - team managed day to day, formally employed by the provider",
                "Turnover, weaker candidate loyalty to the client brand",
                "Ramping a team for a project, flexible headcount",
              ],
            ],
          },
          {
            kind: "p",
            text: "There is no single 'right' model, only one that fits your scale. A company hiring its first person in Poland almost always starts with EOR or B2B, because the cost and time of setting up an entity is not justified by a single headcount. Once the team grows, the math changes.",
          },
        ],
      },
      {
        id: "koszty",
        eyebrow: "02 / Costs",
        title: "What it actually costs: three examples",
        blocks: [
          {
            kind: "table",
            caption: "Total employer cost in Poland, sample gross salaries (PLN/mo, 2026)",
            head: ["Gross salary (employment)", "Total employer cost (employment)", "Total cost via EOR", "B2B invoice equivalent (net)"],
            rows: [
              ["15,000 PLN", "approx. 18,000-18,300 PLN", "approx. 19,500-21,300 PLN", "approx. 16,500-18,000 PLN"],
              ["25,000 PLN", "approx. 30,000-30,500 PLN", "approx. 32,500-35,000 PLN", "approx. 27,000-29,500 PLN"],
              ["35,000 PLN", "approx. 42,000-42,700 PLN", "approx. 45,000-48,500 PLN", "approx. 37,500-41,000 PLN"],
            ],
          },
          {
            kind: "p",
            text: "Employer cost on a Polish employment contract is gross salary plus roughly 20% in social security, labour fund and guarantee fund contributions. An EOR adds its own fee on top (shown here as a percentage of base cost; many providers actually charge a flat monthly fee, which is usually cheaper at higher salaries). B2B looks cheapest at first glance, but the contractor covers their own social security and insurance, hence the higher invoice rate than a simple gross-to-net conversion would suggest.",
          },
          {
            kind: "p",
            text: "A full comparison also needs 26 days of annual paid leave under an employment contract (on B2B this is negotiated contractually, usually 20-26 days of paid time off as part of the deal) and PPK, the employee capital plan, to which the employer standardly contributes 1.5% of gross salary unless the employee has opted out.",
          },
        ],
      },
      {
        id: "ryzyko",
        eyebrow: "03 / Risk",
        title: "Four risks to understand before signing anything",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "B2B contract reclassification",
                meta: "The most common risk",
                body: "If a contractor works exclusively for one client, on fixed hours, under direct supervision and with no business risk of their own, tax authorities can treat the relationship as a disguised employment. The result is back-payment of social security contributions, tax, and penalties for both parties.",
              },
              {
                title: "Permanent establishment",
                meta: "A risk for the parent company",
                body: "Hiring a person in Poland who actually signs contracts or runs operational activity on behalf of the foreign company can create a tax presence for that company in Poland, even without a local branch. A well-structured EOR reduces this risk but does not automatically eliminate it for sales or decision-making roles.",
              },
              {
                title: "IP assignment",
                meta: "Critical for product companies",
                body: "Under Polish copyright law, transfer of rights to code must be explicitly stated in the contract - it does not happen automatically just because someone is employed, especially on B2B. A missing IP assignment clause is one of the most common gaps in contracts signed without local counsel.",
              },
              {
                title: "Data protection and GDPR",
                meta: "Applies to every model",
                body: "Regardless of the employment model, the company remains responsible for GDPR compliance over any data the employee can access. With an EOR, you need a clear split of who is the data controller and who is the processor for HR data.",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / Process",
        title: "From offer to first day: a 30-day plan",
        blocks: [
          {
            kind: "list",
            items: [
              "Days 1-5: pick the model (EOR, B2B, own entity) and, for EOR, sign with a provider that has a real operational presence in Poland, not just a virtual office.",
              "Days 3-10: negotiate terms with the candidate - contract form, range, benefits, holiday equivalent, equipment.",
              "Days 8-12: prepare the contract via the EOR or your legal team (for B2B), including IP assignment and confidentiality clauses.",
              "Days 10-15: formal onboarding - social security registration (for employment via EOR), mandatory occupational health check, equipment and access setup.",
              "Days 15-25: technical onboarding - repository and tool access, first sprint; configure PPK in parallel where applicable.",
              "Day 30: the employee is fully operational, first payroll run on the Polish schedule (typically by the 10th of the following month).",
            ],
          },
          {
            kind: "p",
            text: "By comparison, setting up your own sp. z o.o. in Poland typically takes 6-10 weeks, covering court registration, opening a company bank account, registering as a social security payer and setting up accounting, before you have even hired the first person.",
          },
        ],
      },
      {
        id: "kandydat",
        eyebrow: "05 / Candidate expectations",
        title: "What candidates in Poland actually expect",
        blocks: [
          {
            kind: "p",
            text: "Most senior developers we talk to treat a B2B contract not as an option but as a precondition for even discussing an offer. The reason is simple: a lower effective tax rate (flat-rate or 19% linear tax instead of the progressive PIT scale) and flexibility in expensing business costs.",
          },
          {
            kind: "list",
            items: [
              "A choice between B2B and employment - no B2B option filters out some senior candidates before the range is even discussed.",
              "Payment terms of 7-10 days from invoice on B2B, not 30-45 days - this is often asked directly in the first call.",
              "Equipment delivered before day one, not in week two.",
              "A holiday equivalent comparable to the 26 days under employment, even where the contract does not formally require it.",
              "Clarity on PPK and non-cash benefits (private healthcare, sports card) at the offer stage, not after signing.",
            ],
          },
        ],
      },
    ],
    faqTitle: "Frequently asked questions",
    faq: [
      {
        q: "Is EOR legal in Poland?",
        a: "Yes, employer of record is a legal model where a local entity formally employs a person on behalf of a foreign company. The key is that the EOR provider has genuine registration and HR infrastructure in Poland rather than operating as an empty shell.",
      },
      {
        q: "When does an own entity pay off compared to EOR?",
        a: "Usually above roughly 10 employees over a 2-3 year horizon, once the EOR's flat fee multiplied by headcount exceeds the cost of running your own entity and HR function.",
      },
      {
        q: "Can I hire a developer in Poland on B2B only?",
        a: "Yes, that is the most common form among senior IT specialists, provided the contract genuinely reflects contractor independence: their own business risk, flexible hours, and the ability to work for other clients.",
      },
      {
        q: "How long does it take from offer to first day via EOR?",
        a: "With a competent EOR provider, realistically 5-10 working days for the formal side, plus the notice period the candidate owes their current employer, typically 1-3 months for a Polish employment contract.",
      },
      {
        q: "What happens if a B2B contract is structured incorrectly?",
        a: "On reclassification into an employment relationship, the company may be liable for back social security contributions and tax plus interest, and in some cases administrative penalties. This risk grows with the number of exclusive-use contractors.",
      },
    ],
    cta: ctaEn,
    methodology:
      "Methodology: cost data and timelines are drawn from hiring processes run with Positive Recruitment clients in 2025-2026, cross-checked against public Polish IT salary reports. This is general information, not legal or tax advice; treat the figures and timelines as planning ranges.",
  },
  zh: {
    title: "波兰雇主代理（EOR）：如何在没有本地实体的情况下雇佣程序员",
    h1: "2026 年波兰雇主代理（EOR）指南：EOR、B2B 与自建实体对比",
    metaTitle: "波兰雇主代理 EOR 2026：成本、风险与流程",
    metaDescription:
      "无需在波兰注册公司即可雇佣开发者：EOR、B2B、自建有限公司（sp. z o.o.）与人员外包对比，含真实成本、合规风险与 30 天入职时间表。",
    eyebrow: "洞察 · 用工模式",
    summary:
      "EOR、B2B、自建实体或人员外包：面向没有波兰法律实体的公司的成本、风险与时间对比。",
    lead:
      "越来越多来自西欧和美国的公司想在波兰雇佣工程师，却不想在本地设立任何法律实体。我们在克拉科夫开展 IT 招聘业务，服务范围覆盖全波兰及中东欧地区，因此经常为客户分析：在波兰雇佣第一位员工时，哪种用工模式合理；而在雇到第十位员工时，答案又会完全不同。以下是对四种路径的真实拆解：雇主代理（EOR）、B2B 合同、自建实体，以及人员外包（staff augmentation）。",
    note:
      "本文为一般性信息，不构成法律或税务建议。文中金额为 2026 年的规划性区间（波兰兹罗提/欧元），具体决策前请咨询本地税务顾问或劳动法律师。",
    readTime: "8 分钟阅读",
    takeaways: [
      "在波兰，EOR 可在 5-10 个工作日内完成员工入职；自建实体则需要 6-10 周。",
      "典型的 EOR 费用为薪资的 8%-15%，或每位员工每月 300-800 欧元，需在雇佣成本之外额外支付。",
      "波兰雇主需缴纳的社保及相关基金（ZUS、劳动基金、职工保障基金）约为税前工资的 20%，与用工模式无关。",
      "团队规模超过约 10 人时，自建有限公司（sp. z o.o.）在 2-3 年周期内通常比 EOR 更划算。",
      "结构不当的 B2B 合同（固定工时、仅服务一个客户、无经营风险）存在被重新认定为雇佣关系的风险，可能导致补缴社保及罚款。",
      "波兰大多数资深开发者更偏好 B2B 合同——这往往是达成录用的前提条件，而非可有可无的加分项。",
    ],
    sections: [
      {
        id: "modele",
        eyebrow: "01 / 用工模式",
        title: "在波兰无本地实体情况下的四条用工路径",
        blocks: [
          {
            kind: "table",
            caption: "波兰用工模式对比，2026 年",
            head: ["模式", "落地时间", "额外成本", "控制力", "风险", "适用场景"],
            rows: [
              [
                "雇主代理（EOR）",
                "5-10 个工作日",
                "薪资的 8%-15%，或每月 300-800 欧元",
                "中等--EOR 作为正式雇主",
                "较低，前提是供应商在波兰有真实运营能力",
                "1-9 人团队、市场测试、快速启动",
              ],
              [
                "B2B 合同（直接签约）",
                "1-3 个工作日",
                "无固定费用，但净费率更高",
                "较高——民事合同，雇主义务较少",
                "结构不当时存在重新认定风险",
                "独立承包人、资深个人开发者、中短期合作",
              ],
              [
                "自建实体（sp. z o.o.）",
                "6-10 周",
                "会计、人事及合规成本",
                "完全掌控",
                "法律与行政责任全部由公司承担",
                "团队超过约 10 人、长期在波兰运营",
              ],
              [
                "人员外包 / 派遣（staff augmentation）",
                "1-2 周",
                "供应商在员工成本基础上加收 20%-40% 利润",
                "较低——团队日常由客户管理，正式雇主是供应商",
                "人员流动率高，候选人对客户品牌忠诚度较弱",
                "项目期间快速扩充团队、灵活调整人数",
              ],
            ],
          },
          {
            kind: "p",
            text: "并不存在唯一“正确”的模式，只有与规模匹配的模式。首次在波兰雇人的公司几乎总是从 EOR 或 B2B 起步，因为设立实体的成本和时间在只雇一个人的阶段并不划算。等团队规模扩大后，这个计算逻辑就会改变。",
          },
        ],
      },
      {
        id: "koszty",
        eyebrow: "02 / 成本",
        title: "真实成本是多少：三个示例",
        blocks: [
          {
            kind: "table",
            caption: "波兰雇主总成本示例（税前月薪，波兰兹罗提，2026 年）",
            head: ["税前月薪（雇佣）", "雇主总成本（雇佣）", "通过 EOR 的总成本", "B2B 发票等值（税后）"],
            rows: [
              ["15,000 PLN", "约 18,000-18,300 PLN", "约 19,500-21,300 PLN", "约 16,500-18,000 PLN"],
              ["25,000 PLN", "约 30,000-30,500 PLN", "约 32,500-35,000 PLN", "约 27,000-29,500 PLN"],
              ["35,000 PLN", "约 42,000-42,700 PLN", "约 45,000-48,500 PLN", "约 37,500-41,000 PLN"],
            ],
          },
          {
            kind: "p",
            text: "波兰雇佣合同下的雇主成本是税前工资加上约 20% 的社保、劳动基金及职工保障基金缴纳。EOR 会在此基础上再加收自身的服务费（本文以基础成本的百分比表示，实际上许多供应商采用固定月费，薪资较高时这种方式通常更便宜）。B2B 表面上看起来最便宜，但承包人需自行承担社保和保险，因此发票金额会高于简单的税前税后换算结果。",
          },
          {
            kind: "p",
            text: "完整核算还需考虑雇佣合同下每年 26 天的带薪年假（B2B 模式下则通过合同约定，通常是 20-26 天的带薪休息作为谈判的一部分），以及员工资本计划（PPK）--除非员工选择退出，雇主通常需按税前工资的 1.5% 缴纳配套资金。",
          },
        ],
      },
      {
        id: "ryzyko",
        eyebrow: "03 / 风险",
        title: "签约前必须了解的四类风险",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "B2B 合同重新认定风险",
                meta: "最常见的风险",
                body: "如果承包人只为一个客户工作，工时固定，接受直接监督，且不承担任何经营风险，税务机关可能将该关系认定为“变相雇佣”。后果是需要补缴社保和税款并加收利息，双方都可能面临处罚。",
              },
              {
                title: "常设机构（Permanent Establishment）风险",
                meta: "对母公司的风险",
                body: "如果在波兰雇佣的人员实际上代表境外公司签署合同或开展经营活动，即使没有本地分支机构，也可能使该境外公司在波兰产生纳税义务。结构合理的 EOR 可以降低这一风险，但对于销售或决策类岗位并不能完全消除。",
              },
              {
                title: "知识产权转让（IP Assignment）",
                meta: "对产品型公司至关重要",
                body: "根据波兰著作权法，代码权利的转让必须在合同中明确约定——仅凭雇佣关系本身（尤其是 B2B 模式）并不会自动发生转让。缺少知识产权转让条款，是没有本地法律顾问参与起草的合同中最常见的漏洞之一。",
              },
              {
                title: "数据保护与 GDPR",
                meta: "适用于所有用工模式",
                body: "无论采用哪种用工模式，公司都需对员工可访问的数据承担 GDPR 合规责任。在 EOR 模式下，需要明确界定谁是人事数据的控制者，谁是处理者。",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / 流程",
        title: "从发出录用到首日到岗：30 天计划",
        blocks: [
          {
            kind: "list",
            items: [
              "第 1-5 天：确定用工模式（EOR、B2B 或自建实体）；若选择 EOR，需与在波兰有真实运营能力的供应商签约，而非仅有虚拟办公室的空壳公司。",
              "第 3-10 天：与候选人商定条件——合同形式、薪资区间、福利、年假等值天数、办公设备。",
              "第 8-12 天：由 EOR 或法务团队（B2B 情况下）起草合同，包含知识产权转让条款和保密条款。",
              "第 10-15 天：正式入职手续——社保登记（通过 EOR 雇佣时）、入职前职业健康检查、设备与系统权限配置。",
              "第 15-25 天：业务层面的入职——代码仓库与工具权限、加入第一个冲刺；如适用，同步配置员工资本计划（PPK）。",
              "第 30 天：员工全面到岗，按波兰工资发放周期完成首次薪资结算（通常为次月 10 日前）。",
            ],
          },
          {
            kind: "p",
            text: "相比之下，在波兰自建有限公司（sp. z o.o.）通常需要 6-10 周，包括法院注册、开设公司银行账户、登记为社保缴纳主体、以及搭建会计体系——而这一切都发生在雇到第一个人之前。",
          },
        ],
      },
      {
        id: "kandydat",
        eyebrow: "05 / 候选人诉求",
        title: "波兰候选人真正在意什么",
        blocks: [
          {
            kind: "p",
            text: "我们接触到的大多数资深开发者，会把 B2B 合同视为讨论录用条件的前提，而非可选项。原因很简单：更低的实际税负（按固定税率或 19% 线性税率纳税，而非累进的 PIT 税率），以及在费用抵扣上更大的灵活性。",
          },
          {
            kind: "list",
            items: [
              "可以在 B2B 与雇佣合同之间自由选择——不提供 B2B 选项会在薪资区间讨论之前就流失部分资深候选人。",
              "B2B 模式下发票付款周期为开票后 7-10 天，而非 30-45 天——这一点常常在第一次通话中就被直接问到。",
              "设备在入职首日前配发到位，而不是入职第二周才到手。",
              "即使合同形式上不要求，也希望获得与雇佣合同下 26 天年假相当的休假等值待遇。",
              "在发出 offer 阶段就明确员工资本计划（PPK）及非现金福利（私人医疗、运动卡）的具体安排，而非签约之后才说明。",
            ],
          },
        ],
      },
    ],
    faqTitle: "常见问题",
    faq: [
      {
        q: "在波兰使用 EOR 合法吗？",
        a: "合法。雇主代理（EOR）是一种合法的用工模式，由本地实体代表境外公司正式雇佣员工。关键在于 EOR 供应商在波兰拥有真实的注册资质和人事运营能力，而不是一个空壳结构。",
      },
      {
        q: "什么时候自建实体比使用 EOR 更划算？",
        a: "通常是团队规模超过约 10 人、以 2-3 年为周期计算时——此时按人头计算的 EOR 固定费用总额会超过自建实体及人事职能的运营成本。",
      },
      {
        q: "可以只通过 B2B 合同在波兰雇佣开发者吗？",
        a: "可以，这是波兰资深 IT 专业人士中最常见的形式，前提是合同真实反映承包人的独立性：自行承担经营风险、工时灵活、并可以为其他客户提供服务。",
      },
      {
        q: "通过 EOR 从发出 offer 到员工首日到岗需要多久？",
        a: "在供应商执行到位的情况下，正式流程通常只需 5-10 个工作日；此外还需加上候选人对现雇主的离职通知期，若是波兰雇佣合同，通常为 1-3 个月。",
      },
      {
        q: "如果 B2B 合同结构不当会有什么后果？",
        a: "一旦被重新认定为雇佣关系，公司可能需要补缴社保和税款并加收利息，某些情况下还会面临行政处罚。独家使用的承包人数量越多，这一风险就越高。",
      },
    ],
    cta: ctaZh,
    methodology:
      "研究方法：成本数据与时间表来自 Positive Recruitment 在 2025-2026 年与客户共同推进的实际雇佣流程，并与公开的波兰 IT 薪酬报告进行了交叉核对。本文为一般性信息，不构成法律或税务建议，请将其中的数字和时间表视为规划性区间。",
  },
};
